// (C) 2022 GoodData Corporation
import { invariant } from "ts-invariant";
import { FormatterUsingIcu, InvalidArgumentError, StringCharacterIterator, } from "./fmt_base.js";
import { ResultMetadata } from "./gooddata_format.js";
import { DefaultFormatterMatchers } from "./regex.js";
/**
 * A single arithmetic expression. E.g. `{{{2|2.|##}}}`. The first two arguments indicate how to 'massage' the value
 * to format, the third argument contains the format string to use.
 *
 * The first argument indicates value divisor. This can be any positive float number. The value to format will be
 * divided by this number.
 *
 * The second argument indicates value second argument to modulo operation (std::fmod). This can be any positive
 * integer. The value to format will be fmod-ded.
 *
 * The second argument may also include (or just be) '.'. This indicates whether to trim (floor) the fraction part
 * of the value to format or not.
 *
 * The division, modulo and floor operations are applied in this order; result of the operation is passed to the next
 * operation. If the argument for that operation is not specified on the expression, then the operation is skipped.
 *
 * So for instance `{{{3600|60|#}}}` would do `fmod(value / 3600, 60)`; `{{{|60|#}}}` would do `fmod(value, 60)` and
 * so on.
 *
 * The third argument is the format. This will be compiled business-as-usual and used to format the value after the
 * arithmetic operations.
 */
export class ArithmeticExpressionFormat extends FormatterUsingIcu {
    constructor(formatter, trimFraction, divisor, modulo) {
        super();
        this.divisor = divisor;
        this.modulo = modulo;
        this.trimFraction = trimFraction;
        this.formatter = formatter;
    }
    doFormat(ctx, number, metadata) {
        // for purposes of arithmetic expression evaluation, NULLs are treated as zeroes
        //
        // this follows the reference implementation
        let numberToFormat = number || 0;
        if (this.divisor !== undefined) {
            numberToFormat = numberToFormat / this.divisor;
        }
        if (this.modulo !== undefined) {
            numberToFormat = numberToFormat % this.modulo;
        }
        if (this.trimFraction) {
            // this follows reference impl
            //
            // when trimming fractions, reference impl does `>> 0`; it behaves as follows:
            //
            // - if the previous operations resulted in NaN or Infinity (for any reason, like dividing by zero) then the shift would
            //   make the NaN into 0 :)
            // - if the value is float, the right shift just discards the fraction; important to note, for numbers
            //   such as -0.4, the right shift returns 0
            //
            // the special handling for negative numbers is to have same behavior as reference impl; floor on -0.9 brings
            // number to -1 and not to -0, thus doing the absolute value shenanigans; the extra check for -0 is just
            // to explicitly show that -0 should not be allowed
            if (Number.isNaN(numberToFormat) || !Number.isFinite(numberToFormat)) {
                numberToFormat = 0;
            }
            else if (numberToFormat < 0) {
                numberToFormat = -1 * Math.floor(-1 * numberToFormat);
                if (Object.is(numberToFormat, -0)) {
                    numberToFormat = 0;
                }
            }
            else {
                numberToFormat = Math.floor(numberToFormat);
            }
        }
        return this.formatter.doFormat(ctx, numberToFormat, metadata);
    }
    doResolve(_ctx, _number) {
        // follows reference implementation; no resolve for arithmetic formats
        return null;
    }
}
/**
 * Format strings that include arithmetic expressions are compiled into this class.
 *
 * The arithmetic expression looks somehow like this example:
 *
 * ```
 * {{{2|2.|##}}}
 * ```
 *
 * (see regexes for full detail). A format string may have multiple expressions and furthermore, this can be used in different
 * alternatives of a conditional format. So for instance:
 *
 * ```
 * [>1000]{{{2|2.|##}}};{{{3|3.|##}}}
 * ```
 *
 * The class matches the reference implementation.
 *
 * High-level, compile time goes goes as follows:
 *
 * 1. Build a bare format string where arithmetic expressions are replaced by placeholders; compile bare formatter.
 *    Placeholders are chars from private use area, they start at U+E000 and end at U+E0FF (including). Each
 *    expression has placeholder assigned sequentially.
 *
 * 2. Build per-expression formatters (ArithmeticExpressionFormat class).
 *
 * Then when time comes to format, the code will:
 *
 * 1. Use the bare formatter to format the number (or rather, its absolute value, that is how reference impl does it).
 * 2. Go through the result, look for expression placeholders.
 * 3. For each expression placeholder, find the expression (simple indexing is all that is needed) and use
 *    the expression's formatter to format the number & use the result instead of the placeholder.
 * 4. Done.
 */
class ArithmeticFormat extends FormatterUsingIcu {
    static doubleTostring(value) {
        return Number.parseFloat(value);
    }
    constructor(base_format, expressions) {
        super();
        this.baseFormat = base_format;
        this.expressions = expressions;
    }
    doFormat(ctx, number, metadata) {
        // first apply the base format; normally, this should result in some literal with expression
        // placeholders to resolve; however nothing blocks caller from using any/all features of the format string
        //
        // this mimics the reference implementation
        const nestedCtx = ctx.toNestedFormatCtx();
        const intermediate = this.baseFormat.doFormat(nestedCtx, number, metadata || undefined);
        let result = "";
        const intermediateIterator = new StringCharacterIterator(intermediate, 0, intermediate.length);
        while (intermediateIterator.hasNext()) {
            const segment = intermediateIterator.next();
            const c = segment.charCodeAt(0);
            if (c >= ArithmeticFormat.ExpressionPlaceholderStart &&
                c <= ArithmeticFormat.ExpressionPlaceholderEnd) {
                const expressionIndex = c - ArithmeticFormat.ExpressionPlaceholderStart;
                if (expressionIndex >= this.expressions.length) {
                    // placeholder for expression out of range
                    // pass it into the result as is - garbage-in, garbage-out;
                    result += segment;
                }
                else {
                    const expressionMetadata = new ResultMetadata();
                    result += this.expressions[expressionIndex].doFormat(nestedCtx, number, metadata !== undefined ? expressionMetadata : undefined);
                    if (metadata !== undefined) {
                        // merge in colors used by the expression formatter if they are not already available
                        if (!metadata.colors.color) {
                            metadata.colors.color = expressionMetadata.colors.color;
                        }
                        if (!metadata.colors.customColor) {
                            metadata.colors.customColor = expressionMetadata.colors.customColor;
                        }
                        if (!metadata.colors.customBgColor) {
                            metadata.colors.customBgColor = expressionMetadata.colors.customBgColor;
                        }
                    }
                }
            }
            else {
                result += segment;
            }
        }
        if (metadata !== undefined) {
            metadata.effectiveFormat = undefined;
        }
        return result;
    }
    static compile(matchers, factory, format) {
        const matcher = matchers.getArithmeticMatcher();
        let baseFormat = "";
        const expressions = [];
        let lastMatchEnd = 0;
        let matchResult;
        while ((matchResult = matcher.exec(format)) !== null) {
            const matchBegin = matchResult.index;
            const matchEnd = matcher.lastIndex;
            baseFormat += format.substring(lastMatchEnd, matchBegin);
            lastMatchEnd = matchEnd;
            // Assign expression placeholder character & verify it is still in range
            const expressionPlaceholder = ArithmeticFormat.ExpressionPlaceholderStart + expressions.length;
            if (expressionPlaceholder > ArithmeticFormat.ExpressionPlaceholderEnd) {
                throw new InvalidArgumentError("Too many arithmetic expressions. You can specify at most 256 expressions.");
            }
            const divisor = stringToArgument(DefaultFormatterMatchers.group(matchResult, 1).value, ArithmeticFormat.doubleTostring);
            const modulo = stringToArgument(DefaultFormatterMatchers.group(matchResult, 2).value, ArithmeticFormat.doubleTostring);
            const trim = stringToTrim(DefaultFormatterMatchers.group(matchResult, 3).value);
            const expressionFormat = DefaultFormatterMatchers.group(matchResult, 4).value;
            // Add the placeholder into the base format string
            baseFormat += String.fromCodePoint(expressionPlaceholder);
            // Add the actual expression into expression formatter vector
            expressions.push(new ArithmeticExpressionFormat(factory.compileFormatter(expressionFormat), trim, divisor, modulo));
        }
        baseFormat += format.substring(lastMatchEnd);
        // If this happens, then the factory incorrectly identified that the format string has arithmetics
        // The code went through the whole string and found no expressions; seems like another type of string that
        // should be compiled differently
        invariant(expressions.length !== 0, "No arithmetic expressions found in the format string.");
        return new ArithmeticFormat(factory.compileFormatter(baseFormat), expressions);
    }
    doResolve(_ctx, _number) {
        // follows reference implementation; no resolve for arithmetic formats
        return null;
    }
}
/// first expression placeholder
ArithmeticFormat.ExpressionPlaceholderStart = 0xe000;
/// last expression placeholder (including)
ArithmeticFormat.ExpressionPlaceholderEnd = 0xe0ff;
export { ArithmeticFormat };
function stringToArgument(value, converter) {
    // divisor may be empty, bail out immediately
    if (!value.length) {
        return undefined;
    }
    const parsedDouble = converter(value);
    if (Number.isNaN(parsedDouble)) {
        return undefined;
    }
    return parsedDouble;
}
function stringToTrim(value) {
    // a presence of '.' in the modulo part of the arithmetic expression means that the
    // fraction should be kept as is; otherwise it should be trimmed (floored)
    //
    // if '.' is present, the match group will be non-empty
    return !value || value.length === 0;
}
//# sourceMappingURL=fmt_arithmetic.js.map